import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { addDays, addMonths } from "date-fns";
import { CalendarPlus, ChevronLeft, ChevronRight, Search, Settings2, UserRound, UsersRound } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/Layout";
import { DayView } from "@/components/calendar/DayView";
import { WeekView } from "@/components/calendar/WeekView";
import { AgendaView } from "@/components/calendar/AgendaView";
import { MonthView } from "@/components/calendar/MonthView";
import { AppointmentDialog } from "@/components/calendar/AppointmentDialog";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { dateKey, fmtRo, mondayOf, monthGridStart } from "@/lib/cal";
import { cn } from "@/lib/utils";

const VIEWS = [{ value: "zi", label: "Zi" }, { value: "saptamana", label: "Săptămână" }, { value: "luna", label: "Lună" }, { value: "lista", label: "Listă" }];

const rangeLabel = (view, date) => {
  if (view === "zi") return fmtRo(date, "EEEE, d MMMM yyyy");
  if (view === "luna") return fmtRo(date, "LLLL yyyy");
  const m = mondayOf(date);
  return `${fmtRo(m, "d MMM")} – ${fmtRo(addDays(m, 6), "d MMM yyyy")}`;
};

const EmptySetup = ({ isAdmin }) => (
  <div className="rounded-xl border border-dashed py-16 text-center" data-testid="appointments-setup-empty">
    <UsersRound className="mx-auto h-10 w-10 text-muted-foreground" />
    <p className="mt-3 font-semibold">Adaugă mai întâi personalul și serviciile</p>
    <p className="text-sm text-muted-foreground">Calendarul are nevoie de cel puțin un coleg și un serviciu.</p>
    {isAdmin && <Button asChild className="mt-4" data-testid="setup-settings-button"><Link to="/programari/setari"><Settings2 className="h-4 w-4" /> Personal & servicii</Link></Button>}
  </div>
);

const Toolbar = ({ view, setView, date, setDate, staff, staffFilter, setStaffFilter, search, setSearch, myStaff }) => {
  const shift = (dir) => setDate(view === "luna" ? addMonths(date, dir) : addDays(date, dir * (view === "zi" ? 1 : 7)));
  const mine = myStaff && staffFilter === myStaff.id;
  return (
    <div className="mb-4 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="icon" onClick={() => shift(-1)} data-testid="cal-prev-button"><ChevronLeft className="h-4 w-4" /></Button>
        <Button variant="outline" onClick={() => setDate(new Date())} data-testid="cal-today-button">Azi</Button>
        <Button variant="outline" size="icon" onClick={() => shift(1)} data-testid="cal-next-button"><ChevronRight className="h-4 w-4" /></Button>
        <span className="ml-1 font-display text-lg font-semibold capitalize" data-testid="cal-range-label">{rangeLabel(view, date)}</span>
        <Input type="date" value={dateKey(date)} onChange={(e) => e.target.value && setDate(new Date(`${e.target.value}T00:00`))} className="h-9 w-40 bg-card" data-testid="cal-date-input" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {myStaff && (
          <Button variant={mine ? "default" : "outline"} onClick={() => setStaffFilter(mine ? "all" : myStaff.id)} data-testid="cal-my-calendar-button">
            <UserRound className="h-4 w-4" /> {mine ? "Calendarul meu" : "Doar eu"}
          </Button>
        )}
        <div className="flex rounded-lg border bg-card p-1" data-testid="cal-view-toggle">
          {VIEWS.map((v) => (
            <button key={v.value} onClick={() => setView(v.value)} data-testid={`cal-view-${v.value}`}
              className={cn("rounded-md px-3 py-1.5 text-sm font-medium transition-colors", view === v.value ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")}>{v.label}</button>
          ))}
        </div>
        <Select value={staffFilter} onValueChange={setStaffFilter}>
          <SelectTrigger className="h-10 w-48 bg-card" data-testid="cal-staff-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all" data-testid="cal-staff-filter-all">Toată echipa</SelectItem>
            {staff.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`cal-staff-filter-${s.id}`}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {view === "zi" && staffFilter === "all" && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Caută coleg..." className="h-10 w-44 bg-card pl-9" data-testid="cal-staff-search" />
          </div>
        )}
      </div>
    </div>
  );
};

export default function Appointments() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [view, setView] = useState("zi");
  const [date, setDate] = useState(new Date());
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [appts, setAppts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [staffFilter, setStaffFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [dlg, setDlg] = useState({ open: false, appt: null, preset: null });
  const [myStaff, setMyStaff] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/staff"), api.get("/services"), api.get("/staff/me")]).then(([s, v, me]) => {
      setStaff(s.data); setServices(v.data); setLoaded(true);
      if (me.data) { setMyStaff(me.data); setStaffFilter(me.data.id); }
    });
  }, []);

  const range = useMemo(() => {
    if (view === "luna") { const s = monthGridStart(date); return [dateKey(s), dateKey(addDays(s, 42))]; }
    const start = view === "zi" ? date : mondayOf(date);
    return [dateKey(start), dateKey(addDays(start, view === "zi" ? 1 : 7))];
  }, [view, date]);

  const load = useCallback(() => api.get("/appointments", { params: { start: range[0], end: range[1] } }).then((r) => setAppts(r.data)), [range]);
  useEffect(() => { load(); }, [load]);

  const staffById = useMemo(() => Object.fromEntries(staff.map((s) => [s.id, s])), [staff]);
  const selectedStaff = staffFilter === "all" ? null : staffById[staffFilter];
  const shownAppts = selectedStaff ? appts.filter((a) => a.staff_id === selectedStaff.id) : appts;
  const dayStaff = selectedStaff ? [selectedStaff] : staff.filter((s) => s.name.toLowerCase().includes(search.trim().toLowerCase()));

  const openNew = (s, start) => setDlg({ open: true, appt: null, preset: { staff_id: s?.id || "", start } });
  const openAppt = (a) => setDlg({ open: true, appt: a, preset: null });
  const openDay = (d) => { setDate(d); setView("zi"); };

  const move = async (id, patch, force = false) => {
    const a = appts.find((x) => x.id === id);
    if (!a) return;
    const start = patch.date ? `${patch.date}${a.start.slice(10)}` : patch.start;
    const staffId = patch.staff_id || a.staff_id;
    if (start === a.start && staffId === a.staff_id) return;
    const payload = { start, force, ...(staffId !== a.staff_id && { staff_id: staffId }) };
    setAppts((list) => list.map((x) => (x.id === id ? { ...x, start, staff_id: staffId } : x)));
    try {
      await api.put(`/appointments/${id}`, payload);
      toast.success("Programare mutată");
    } catch (e) {
      const w = e.response?.status === 409 ? e.response.data.detail?.warnings || [] : null;
      if (w && window.confirm(`Atenție:\n• ${w.join("\n• ")}\n\nMuți oricum?`)) return move(id, patch, true).then(load);
      if (!w) toast.error(formatApiError(e));
    }
    load();
  };

  return (
    <div data-testid="appointments-page">
      <PageHeader title="Programări" subtitle="Calendarul intern al echipei. Click pe un interval liber pentru a programa, trage o programare pentru a o muta.">
        {isAdmin && <Button asChild variant="outline" data-testid="schedule-settings-button"><Link to="/programari/setari"><Settings2 className="h-4 w-4" /> Personal & servicii</Link></Button>}
        <Button onClick={() => openNew(selectedStaff, `${dateKey(date)}T09:00`)} disabled={!staff.length || !services.length} data-testid="new-appointment-button">
          <CalendarPlus className="h-4 w-4" /> Programare nouă
        </Button>
      </PageHeader>
      {loaded && (!staff.length || !services.length) ? <EmptySetup isAdmin={isAdmin} /> : (
        <>
          <Toolbar view={view} setView={setView} date={date} setDate={setDate} staff={staff} staffFilter={staffFilter} setStaffFilter={setStaffFilter} search={search} setSearch={setSearch} myStaff={myStaff} />
          {view === "zi" && <DayView date={date} staff={dayStaff} appts={shownAppts} onSlot={openNew} onEvent={openAppt} onMove={move} />}
          {view === "saptamana" && <WeekView weekStart={mondayOf(date)} appts={shownAppts} staffById={staffById} selectedStaff={selectedStaff} onSlot={openNew} onEvent={openAppt} onMove={move} />}
          {view === "luna" && <MonthView month={date} appts={shownAppts} staffById={staffById} onEvent={openAppt} onDayClick={openDay} onMove={move} />}
          {view === "lista" && <AgendaView weekStart={mondayOf(date)} appts={shownAppts} staffById={staffById} onEvent={openAppt} />}
        </>
      )}
      <AppointmentDialog open={dlg.open} onOpenChange={(o) => setDlg((d) => ({ ...d, open: o }))} appt={dlg.appt} preset={dlg.preset}
        staff={staff} services={services} onSaved={load} isAdmin={isAdmin} />
    </div>
  );
}
